import {
  Box,
  SimpleGrid,
  Spinner,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
} from '@elkaso-app/web-design';
import { useGetCrmSupplierContactQuotaStatsApi } from 'apis/crm-supplier-contact-quotas';
import React from 'react';

interface StatCardProps {
  title: string;
  value: string | number;
  helpText?: string;
  trend?: 'increase' | 'decrease';
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, helpText, trend, isLoading }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box
      p={6}
      bg={bgColor}
      border='1px solid'
      borderColor={borderColor}
      borderRadius='lg'
      shadow='sm'
      transition='all 0.2s'
      _hover={{ shadow: 'md' }}>
      <Stat>
        <StatLabel fontSize='sm' fontWeight='medium' color='gray.600'>
          {title}
        </StatLabel>
        <StatNumber fontSize='2xl' fontWeight='bold' color='gray.800'>
          {isLoading ? <Spinner size='sm' /> : value}
        </StatNumber>
        {helpText && (
          <StatHelpText mb={0} fontSize='xs' color='gray.500'>
            {trend && <StatArrow type={trend} />}
            {helpText}
          </StatHelpText>
        )}
      </Stat>
    </Box>
  );
};

export const StatsCards: React.FC = () => {
  const { data: stats, isLoading } = useGetCrmSupplierContactQuotaStatsApi();

  if (!stats && !isLoading) return null;

  return (
    <Box mb={6}>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <StatCard
          title='Total Quotas'
          value={stats?.totalQuotas || 0}
          helpText='Active supplier quotas'
          isLoading={isLoading}
        />
        <StatCard
          title='Total Allocated'
          value={stats?.totalAllocated || 0}
          helpText='Total contact quota allocated'
          isLoading={isLoading}
        />
        <StatCard
          title='Total Used'
          value={stats?.totalUsed || 0}
          helpText='Contacts revealed so far'
          isLoading={isLoading}
        />
        <StatCard
          title='Usage Rate'
          value={stats?.overallUsagePercentage || '0%'}
          helpText='Overall quota utilization'
          trend={stats && parseFloat(stats.overallUsagePercentage) > 50 ? 'increase' : undefined}
          isLoading={isLoading}
        />
      </SimpleGrid>

      {stats && (
        <Box mt={4} p={4} bg='blue.50' borderRadius='md' border='1px solid' borderColor='blue.200'>
          <Text fontSize='sm' color='blue.800' textAlign='center'>
            <strong>Average Quota per Supplier:</strong> {stats.averageQuotaPerSupplier} contacts
          </Text>
        </Box>
      )}
    </Box>
  );
};
